import { rem } from 'polished';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';

import { translate } from '@vimeo/i18n-runtime';
import { Button, Input, Notice } from '@vimeo/iris/components';
import { Header } from '@vimeo/iris/typography';

import { EmbedShowcaseContext } from './context/useEmbedShowcase';

type Props = {
  albumId: number;
  xsrft: string;
  onSuccess: (hashedPass: string) => void;
};

function PasswordPrompt({ albumId, xsrft, onSuccess }: Props) {
  const [password, setPassword] = useState('');
  const [showAttemptFailure, setShowAttemptFailure] = useState(false);
  const [hasPendingAttempt, setHasPendingAttempt] = useState(false);
  const embedShowcaseData = useContext(EmbedShowcaseContext);
  const handleInputChange = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      setPassword(event.currentTarget.value);
    },
    [password, setPassword],
  );

  const validatePasswordRequest = (formData: FormData) => {
    let fetchUrl;
    const options: RequestInit = {
      credentials: 'include',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    };

    if (embedShowcaseData?.isShowcaseEmbeded) {
      fetchUrl = `/showcase/${albumId}/password?password=${password}`;
      options.method = 'GET';
    } else {
      fetchUrl = location.pathname === '/' ? '/auth' : `/showcase/${albumId}/auth`;
      options.method = 'POST';
      options.body = formData;
    }

    return fetch(fetchUrl, options);
  };

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setHasPendingAttempt(true);

      const data = {
        password,
        token: xsrft,
        referer_url: `/showcase/${albumId}`,
      };

      const formData = new FormData();
      for (const [key, val] of Object.entries(data)) {
        formData.append(key, val);
      }

      validatePasswordRequest(formData)
        .then((res) => {
          if (!res.ok) {
            setShowAttemptFailure(true);
          }
          return res.json();
        })
        .then((res) => {
          if (res.password === false) {
            setShowAttemptFailure(true);
          }
          if (res.error_code === null) {
            onSuccess(res.hashed_pass);
          }
          setHasPendingAttempt(false);
        })
        .catch(() => {
          setHasPendingAttempt(false);
        });
    },
    [password, showAttemptFailure, setShowAttemptFailure],
  );

  useEffect(() => {
    if (showAttemptFailure) {
      setShowAttemptFailure(false);
    }
  }, [password]);

  return (
    <Form onSubmit={handleSubmit}>
      <PromptMessage>{translate('This showcase is private')}</PromptMessage>
      <Row>
        <PasswordInput
          size="md"
          type="password"
          id={`${albumId}`}
          value={password}
          onChange={handleInputChange}
          placeholder={translate('Enter password')}
          aria-label={translate('Enter password')}
          disabled={hasPendingAttempt}
        />
        <SubmitButton disabled={hasPendingAttempt} size="md" format="primary">
          {translate('Submit')}
        </SubmitButton>
      </Row>
      {showAttemptFailure && (
        <NoticeStyled format="negative" role="alert">
          {translate('Sorry, that password was incorrect. Please try again.')}
        </NoticeStyled>
      )}
    </Form>
  );
}

export default PasswordPrompt;

const Form = styled.form`
  width: 100%;
  text-align: center;
  flex: 1;
  display: flex;
  flex-flow: column;
  justify-content: center;
  align-items: center;
`;

const PromptMessage = styled(Header).attrs({ size: '1' })`
  max-width: none;
`;

const Row = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: center;
  align-items: flex-end;
  padding: 0 ${rem(50)};
  width: 100%;
`;

const PasswordInput = styled(Input)`
  min-width: ${rem(320)};
`;

const SubmitButton = styled(Button)`
  width: ${rem(320)};
  margin: ${rem(15)} 0 0;
  @media screen and (min-width: ${rem(768)}) {
    margin-left: ${rem(15)};
    width: auto;
  }
`;

const NoticeStyled = styled(Notice)`
  width: ${rem(320)};
  margin: ${rem(25)} 0 0;
  @media screen and (min-width: ${rem(768)}) {
    width: auto;
  }
`;
